import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, CalendarClock, Check, FilePlus2, Link2, MailPlus, Paperclip, Reply, Send, ShieldAlert, X } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, PageHeader, ReasonsList, RestrictedState, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { MAIL6_ATT, MAIL6_CAND, MAIL6_CAT, MAIL6_STATUS, fmtDateTime } from "../lib/labels";

const VIEWS = [
  ["requiere_accion", "Requiere mi acción"], ["con_fecha_limite", "Con fecha límite"],
  ["pendiente_respuesta", "Pendiente de respuesta"], ["documentos_recibidos", "Documentos recibidos"],
  ["sin_clasificar", "Sin clasificar"], ["contratos", "Contratos"], ["proyectos", "Proyectos"],
  ["finanzas", "Finanzas"], ["reuniones", "Reuniones"], ["polizas", "Pólizas"],
  ["archivables", "Archivables"], ["posibles_duplicados", "Posibles duplicados"],
];

const SIM = {
  factura: { sender_name: "Construcciones Levante", sender_email: "facturas@levante-demo.es", subject: "Factura F-2026-118 — Certificación junio", body: "Adjuntamos factura F-2026-118 por 31.800 EUR. Vencimiento a 30 días.", attachments: [{ filename: "Factura F-2026-118.pdf", content: `FACTURA F-2026-118 ${Date.now()}` }] },
  contrato: { sender_name: "Asesoría Grau", sender_email: "legal@grau-demo.es", subject: "Borrador contrato de arrendamiento — local 2", body: "Enviamos borrador del contrato de arrendamiento para revisión.", attachments: [{ filename: "Borrador contrato local 2.docx", content: `BORRADOR CONTRATO ${Date.now()}` }] },
  municipal: { sender_name: "Ayuntamiento — Urbanismo", sender_email: "urbanismo@ayuntamiento-demo.es", subject: "Requerimiento municipal — documentación adicional", body: "Se requiere documentación adicional en el expediente urbanístico. Plazo: 15 días hábiles." },
  confirmacion: { sender_name: "Aceros del Nord", sender_email: "calidad@acerosnord-demo.es", subject: "Confirmación de entrega — certificado", body: "Confirmamos la entrega del certificado solicitado." },
  cambio_bancario: { sender_name: "Proveedor Demo", sender_email: "admin@proveedor-demo.es", subject: "Actualización de datos bancarios — nuevo IBAN", body: "Comunicamos el cambio de nuestra cuenta bancaria. Nuevo IBAN ES00 0000 0000 0000 0000 0000. Actualicen los datos bancarios." },
  poliza: { sender_name: "Aseguradora Penedès", sender_email: "renewals@penedes-demo.es", subject: "Póliza RC — aviso de vencimiento", body: "La póliza de responsabilidad civil vence en 30 días. Adjuntamos propuesta de renovación de la póliza.", attachments: [{ filename: "Aviso poliza.pdf", content: `AVISO POLIZA ${Date.now()}` }] },
  ambiguo: { sender_name: "Contacto Varios", sender_email: "varios@demo.es", subject: "Varias cosas", body: "Hola, quería comentaros varias cosas. Ya me decís." },
};

export default function Correo() {
  const { user } = useAuth();
  const { map } = useUsers();
  const qc = useQueryClient();
  const [view, setView] = useState("requiere_accion");
  const [sel, setSel] = useState(null);
  const [dlg, setDlg] = useState(null); // {kind, ...}
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);

  const refresh = () => qc.invalidateQueries();
  const { data: bandeja, error: bandejaErr } = useQuery({ queryKey: ["mail", view], queryFn: async () => (await api.get("/correo/bandeja", { params: { view } })).data });
  const { data: det } = useQuery({ queryKey: ["mail-thread", sel], queryFn: async () => (await api.get(`/correo/threads/${sel}`)).data, enabled: !!sel });

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDlg(null); setF({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error"); },
  });
  const act = (tid, action, extra = {}) => mut.mutate({ path: `/correo/threads/${tid}/accion`, body: { action, ...extra } });
  const open = (d) => { setDlg(d); setF({}); setErr(null); };
  const t = det?.thread;

  if (user?.is_contractor || bandejaErr?.response?.status === 403) {
    return (
      <div className="space-y-4" data-testid="correo-page">
        <PageHeader title="Correo — Smart Inbox (mock)" subtitle="" testid="correo-title" />
        <RestrictedState module="el correo interno" detail={bandejaErr?.response?.data?.detail} testid="correo-restricted" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="correo-page">
      <PageHeader title="Correo — Smart Inbox (mock)"
        subtitle="El thread es la unidad de trabajo. La IA propone clasificación, fechas y acciones; una persona confirma antes de crear tareas o enviar. Nada se archiva con asuntos abiertos."
        testid="correo-title"
        actions={<Button onClick={() => open({ kind: "sim" })} data-testid="mail-sim-btn"><MailPlus className="mr-1 h-4 w-4" /> Simular email recibido</Button>} />

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="space-y-1" data-testid="mail-views">
          {VIEWS.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
              className={`flex w-full items-center justify-between rounded-md px-3 py-1.5 text-xs font-medium ${view === k ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
              <span>{l}</span><span className="font-bold">{bandeja?.views?.[k] ?? "—"}</span>
            </button>
          ))}
        </div>

        <div className="space-y-2" data-testid="mail-threads">
          {(bandeja?.threads || []).map((th) => (
            <button key={th.id} onClick={() => setSel(th.id)} data-testid={`thread-${th.id}`}
              className={`w-full rounded-md border bg-white p-3 text-left ${sel === th.id ? "border-blue-500" : "border-slate-200"}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-slate-800">{th.subject}</span>
                <Chip map={MAIL6_STATUS} value={th.status} testid={`thread-status-${th.id}`} />
                {th.categoria ? <Chip map={MAIL6_CAT} value={th.categoria} /> : <ToneChip label="sin clasificar" tone="warning" />}
                {th.confidentiality === "confidencial" && <ToneChip label="confidencial" tone="warning" />}
                {th.has_dup_candidate && <ToneChip label="adjunto duplicado" tone="danger" testid={`thread-dup-${th.id}`} />}
                {(th.labels || []).includes("rebote") && <ToneChip label="rebote" tone="danger" />}
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {th.participants?.[0]?.name} · {fmtDateTime(th.last_message_at)}
                {(th.deadline || th.deadline_candidate) && !th.deadline_attended ? ` · límite ${(th.deadline || th.deadline_candidate).slice(0, 10)}${th.deadline ? "" : " (candidata)"}` : ""}
                {th.attachments_count ? ` · ${th.attachments_count} adjunto(s)` : ""}
              </p>
            </button>
          ))}
          {bandeja && !bandeja.threads.length && <p className="text-sm text-slate-500" data-testid="mail-empty">Sin threads en esta vista.</p>}
        </div>
      </div>

      {t && (
        <div className="space-y-3 rounded-md border border-slate-300 bg-slate-50 p-4" data-testid="mail-detail">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold text-slate-900">{t.subject}</h2>
            <Chip map={MAIL6_STATUS} value={t.status} />
            {t.categoria && <Chip map={MAIL6_CAT} value={t.categoria} />}
          </div>
          <p className="text-xs text-slate-600" data-testid="mail-summary">{t.summary}</p>
          {t.classification && (
            <p className="rounded border border-indigo-100 bg-indigo-50 p-2 text-xs text-indigo-800" data-testid="mail-classification">
              Propuesta IA (mock): {MAIL6_CAT[t.classification.categoria]?.label} · confianza {Math.round(t.classification.confidence * 100)}% · fuente: {t.classification.source}
              {t.classification.validation_status === "validado" ? ` · validada por ${userName(map, t.classification.validated_by)}` : " · pendiente de validación humana"}
            </p>
          )}

          {t.action_candidates?.length > 0 && (
            <div className="space-y-1" data-testid="mail-candidates">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Acciones candidatas (requieren confirmación humana)</h3>
              {t.action_candidates.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-white p-2" data-testid={`cand-${c.id}`}>
                  <Chip map={MAIL6_CAND} value={c.status} />
                  <span className="text-xs">{c.title}</span>
                  {c.severity === "critica" && <ToneChip label="crítica" tone="danger" />}
                  {c.status === "pendiente" && (
                    <>
                      <Button size="sm" className="h-6 px-2 text-xs" data-testid={`cand-confirm-${c.id}`} onClick={() => act(t.id, "confirmar_accion", { candidate_id: c.id })}><Check className="mr-1 h-3 w-3" /> Confirmar y crear tarea</Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`cand-discard-${c.id}`} onClick={() => open({ kind: "descartar", thread: t, cand: c })}><X className="mr-1 h-3 w-3" /> Descartar</Button>
                    </>
                  )}
                  {c.task_id && <span className="text-xs text-emerald-700">→ tarea {c.task_id}</span>}
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2" data-testid="mail-messages">
            {det.messages.map((m) => (
              <div key={m.id} className="rounded border border-slate-200 bg-white p-3" data-testid={`msg-${m.id}`}>
                <p className="text-xs text-slate-500"><strong>{m.sender_name}</strong> ({m.direction}{m.status === "rebote" ? " · REBOTE" : ""}) → {(m.recipients || []).join(", ")} · {fmtDateTime(m.at)}</p>
                <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{m.body}</p>
                {m.status === "borrador" && (
                  <div className="mt-2 flex items-center gap-2 rounded border border-amber-200 bg-amber-50 p-2" data-testid={`draft-${m.id}`}>
                    <ShieldAlert className="h-4 w-4 text-amber-600" />
                    <span className="text-xs text-amber-800">Borrador (IA mock). Revisa destinatarios, asunto, contenido, adjuntos y confidencialidad antes de enviar.</span>
                    <Button size="sm" className="h-6 px-2 text-xs" data-testid={`send-${m.id}`} onClick={() => open({ kind: "enviar", thread: t, draft: m })}><Send className="mr-1 h-3 w-3" /> Revisar y enviar</Button>
                  </div>
                )}
                {(m.attachments || []).map((a) => (
                  <div key={a.id} className="mt-2 flex flex-wrap items-center gap-2 rounded border border-slate-100 bg-slate-50 p-2" data-testid={`att-${a.id}`}>
                    <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-xs font-medium">{a.filename}</span>
                    <Chip map={MAIL6_ATT} value={a.status} testid={`att-status-${a.id}`} />
                    {a.status === "sin_procesar" && (
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`att-proc-${a.id}`}
                        onClick={() => act(t.id, "procesar_adjunto", { message_id: m.id, attachment_id: a.id })}>Procesar adjunto</Button>
                    )}
                    {a.status === "candidato_duplicado" && (
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-emerald-700" data-testid={`att-link-${a.id}`}
                        onClick={() => act(t.id, "confirmar_vinculo_adjunto", { message_id: m.id, attachment_id: a.id })}>
                        <Link2 className="mr-1 h-3 w-3" /> Es el documento existente: relacionar sin copiar</Button>
                    )}
                    {a.document_id && <span className="text-xs text-slate-500">→ {a.document_id}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5" data-testid="mail-actions">
            {!t.categoria && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-clasificar" onClick={() => open({ kind: "clasificar", thread: t })}>Clasificar</Button>}
            {(t.deadline_candidate && !t.deadline) && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-fecha" onClick={() => act(t.id, "confirmar_fecha")}><CalendarClock className="mr-1 h-3 w-3" /> Confirmar fecha límite ({t.deadline_candidate.slice(0, 10)})</Button>}
            {t.deadline && !t.deadline_attended && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-atender" onClick={() => act(t.id, "atender_fecha")}>Marcar fecha atendida</Button>}
            {!t.expediente_id && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-expediente" onClick={() => act(t.id, "crear_expediente")}><FilePlus2 className="mr-1 h-3 w-3" /> Crear expediente</Button>}
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-evidencia" onClick={() => open({ kind: "evidencia", thread: t })}>Usar como evidencia</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-responder" onClick={() => act(t.id, "preparar_respuesta")}><Reply className="mr-1 h-3 w-3" /> Preparar respuesta</Button>
            {!["archivado", "no_relevante"].includes(t.status) && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="act-archivar" onClick={() => act(t.id, "archivar")}><Archive className="mr-1 h-3 w-3" /> Archivar</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="act-norel" onClick={() => open({ kind: "norel", thread: t })}>No relevante</Button>
              </>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent data-testid="mail-dialog">
          <DialogHeader><DialogTitle>
            {dlg?.kind === "sim" && "Simular email recibido (mock)"}
            {dlg?.kind === "clasificar" && `Clasificar — ${dlg.thread.subject}`}
            {dlg?.kind === "descartar" && "Descartar acción candidata"}
            {dlg?.kind === "evidencia" && "Usar thread como evidencia"}
            {dlg?.kind === "enviar" && "Confirmar envío externo"}
            {dlg?.kind === "norel" && "Marcar como no relevante"}
          </DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {dlg.kind === "sim" && (
                <div><Label>Tipo de disparador demo</Label>
                  <Select value={f.kind || ""} onValueChange={(v) => setF({ ...f, kind: v })}>
                    <SelectTrigger data-testid="f-simkind"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>{Object.keys(SIM).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dlg.kind === "clasificar" && (
                <div><Label>Categoría (validación humana de la propuesta IA)</Label>
                  <Select value={f.categoria || ""} onValueChange={(v) => setF({ ...f, categoria: v })}>
                    <SelectTrigger data-testid="f-categoria"><SelectValue placeholder="Categoría" /></SelectTrigger>
                    <SelectContent>{Object.entries(MAIL6_CAT).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {["descartar", "norel"].includes(dlg.kind) && (
                <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
              )}
              {dlg.kind === "evidencia" && (
                <div><Label>ID del requisito de evidencia *</Label><Input value={f.requirement_id || ""} onChange={(e) => setF({ ...f, requirement_id: e.target.value })} data-testid="f-req" placeholder="evr_…" /></div>
              )}
              {dlg.kind === "enviar" && (
                <>
                  <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="send-review">
                    <p><strong>Para:</strong> {dlg.draft.recipients.join(", ")}</p>
                    <p><strong>Asunto:</strong> {dlg.draft.subject}</p>
                    <p><strong>Confidencialidad:</strong> {dlg.thread.confidentiality}</p>
                    <p className="mt-1 whitespace-pre-wrap">{dlg.draft.body}</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid="f-confirm" /> He revisado destinatarios, asunto, contenido, adjuntos y confidencialidad. Confirmo el envío (mock).</label>
                </>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="mail-error">{apiError(err)}</p>)}
              <Button data-testid="mail-dialog-submit" disabled={mut.isPending}
                onClick={() => {
                  if (dlg.kind === "sim") mut.mutate({ path: "/correo/recibir", body: SIM[f.kind] });
                  else if (dlg.kind === "clasificar") act(dlg.thread.id, "clasificar", { categoria: f.categoria });
                  else if (dlg.kind === "descartar") act(dlg.thread.id, "descartar_accion", { candidate_id: dlg.cand.id, motivo: f.motivo });
                  else if (dlg.kind === "evidencia") act(dlg.thread.id, "usar_como_evidencia", { requirement_id: f.requirement_id });
                  else if (dlg.kind === "enviar") act(dlg.thread.id, "confirmar_envio", { draft_id: dlg.draft.id, confirm: !!f.confirm });
                  else if (dlg.kind === "norel") act(dlg.thread.id, "marcar_no_relevante", { motivo: f.motivo });
                }}>Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
